import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useServices } from '../context/ServiceContext';
import BrandedLoader from './ui/BrandedLoader';

const normalizeProductCode = (value) => String(value || '').trim().toUpperCase().replace(/-/g, '_');
const SERVICE_PRODUCT_CODES = new Set(['WHATSAPP', 'AI_CHAT', 'CRM', 'CUSTOMERGRAPH', 'HRMS', 'JOBS', 'ANALYTICS', 'EMAIL_MARKETING', 'HOSTING']);

const RECOVERY_PATHS = {
  WHATSAPP: ['/products/whatsapp/subscription', '/products/whatsapp/numbers'],
  AI_CHAT: ['/products/ai-chat/subscription'],
  CRM: ['/products/crm/subscription'],
  CUSTOMERGRAPH: ['/products/customergraph/subscription'],
  HRMS: ['/products/hrms/subscription'],
  JOBS: ['/products/jobs/subscription'],
  ANALYTICS: ['/products/analytics/subscription'],
  EMAIL_MARKETING: ['/products/email-marketing/subscription'],
  HOSTING: ['/products/hosting/subscription'],
};

const DEFAULT_RECOVERY_PATH = {
  WHATSAPP: '/products/whatsapp/subscription',
  AI_CHAT: '/products/ai-chat/subscription',
  CRM: '/products/crm/subscription',
  CUSTOMERGRAPH: '/products/customergraph/subscription',
  HRMS: '/products/hrms/subscription',
  JOBS: '/products/jobs/subscription',
  ANALYTICS: '/products/analytics/subscription',
  EMAIL_MARKETING: '/products/email-marketing/subscription',
  HOSTING: '/products/hosting/subscription',
};

const pathMatches = (pathname, targetPath) => {
  if (!targetPath) return false;
  return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
};

const buildRedirectTarget = (basePath, location, productCode) => {
  const next = encodeURIComponent(`${location.pathname}${location.search || ''}`);
  const separator = basePath.includes('?') ? '&' : '?';
  return `${basePath}${separator}locked=${productCode}&next=${next}`;
};

const PermissionRoute = ({ permission, productAccess }) => {
  const { user, loading, hasProductAccess } = useAuth();
  const services = useServices();
  const location = useLocation();
  if (loading) return <BrandedLoader title="Opening your workspace" message="Checking your team role and permissions." />;

  const allowedByPermission = !permission || user?.permissions?.includes(permission);
  const requiredProducts = Array.isArray(productAccess)
    ? productAccess
    : productAccess
      ? [productAccess]
      : [];
  const allowedByProduct = !requiredProducts.length || requiredProducts.some((product) => hasProductAccess?.(normalizeProductCode(product)));

  if (!allowedByPermission || !allowedByProduct) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!services?.loading) {
    const gatedProducts = requiredProducts
      .map(normalizeProductCode)
      .filter((productCode) => SERVICE_PRODUCT_CODES.has(productCode));

    const waitingForServiceRecords = gatedProducts.some((productCode) => !services?.getService?.(productCode));
    if (waitingForServiceRecords) {
      return <BrandedLoader title="Syncing product access" message="Loading workspace subscriptions and service records." />;
    }

    const blockedProduct = gatedProducts.find((productCode) => {
      const access = services?.getServiceAccess?.(productCode);
      const recoveryPaths = [
        access?.billingGate?.actionPath,
        ...(RECOVERY_PATHS[productCode] || []),
      ].filter(Boolean);

      if (recoveryPaths.some((path) => pathMatches(location.pathname, path))) {
        return false;
      }

      return !access?.active;
    });

    if (blockedProduct) {
      const access = services?.getServiceAccess?.(blockedProduct);
      const target = access?.billingGate?.actionPath || DEFAULT_RECOVERY_PATH[blockedProduct] || '/services';
      return <Navigate to={buildRedirectTarget(target, location, blockedProduct)} replace />;
    }
  }

  return <Outlet />;
};

export default PermissionRoute;
